import { TaxId } from '@vrplatform/components';
import { Badge } from '@vrplatform/components';
import type { gqlV2 } from '@vrplatform/data';
import {
  BriefcaseIcon,
  EmailIcon,
  HashtagIcon,
  LoaderIcon,
  PhoneIcon,
  PinIcon,
} from '@vrplatform/icons';
import { Icon } from '@vrplatform/icons';
import { useAppName } from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import { Box, Center, Group, Tooltip, useMantineTheme } from '@mantine/core';
import type { activeStatus_enum } from '@vrplatform/graphql';
import { formatAddress } from '@vrplatform/ui-common';
import type { ComponentProps } from 'react';
import { DrawerInfoCard } from '../../_components';
import type { OwnerDetail } from '../../owner-drawers/useOwnerDetailDrawerQuery';

type Props = {
  owner: {
    companyType: string | undefined;
    status: Maybe<activeStatus_enum>;
    pmsStatus: Maybe<activeStatus_enum>;
    address: OwnerDetail['address'];
    email: Maybe<string>;
    phone: Maybe<string>;
    taxId: Maybe<string>;
    payoutAccountName: Maybe<string>;
  };
};

export const LegacyOwnerInfoCard = ({ owner }: Props) => {
  return (
    <DrawerInfoCard
      rows={[
        {
          icon: LoaderIcon,
          title: 'Status',
          text: (owner.status === 'inactive' ||
            owner.pmsStatus === 'inactive') && <StatusBadge {...owner} />,
        },
        {
          icon: BriefcaseIcon,
          title: 'Type',
          text: owner.companyType,
        },
        {
          icon: HashtagIcon,
          title: 'Tax ID',
          text: owner.taxId?.trim() ? <TaxId taxId={owner.taxId} /> : null,
        },
        {
          icon: EmailIcon,
          title: 'Email',
          text: owner.email || null,
        },
        {
          icon: PhoneIcon,
          title: 'Phone',
          text: owner.phone || null,
        },
        {
          icon: PinIcon,
          title: 'Address',
          text: formatAddress(owner.address) || null,
        },
      ]}
    />
  );
};

const StatusBadge = (data: {
  pmsStatus: Maybe<gqlV2.activeStatus_enum>;
  status: Maybe<gqlV2.activeStatus_enum>;
}) => {
  if (data.pmsStatus === 'inactive' && !data.status) return <PmsStatus />;

  return <OwnerStatus {...data} />;
};

const PmsStatus = () => {
  return (
    <Badge
      color="orange"
      leftIcon={
        <Icon
          icon="AlertTriangleIcon"
          size={12}
          color={(theme) => theme.colors.orange[8]}
        />
      }
    >
      Disabled in PMS
    </Badge>
  );
};

const OwnerStatus = (data: ComponentProps<typeof StatusBadge>) => {
  const { colors } = useMantineTheme();
  const { appName } = useAppName();

  const isDisabledInPms = data.pmsStatus === 'inactive';

  if (data.status === 'inactive') {
    return (
      <Group wrap="nowrap" gap={'xs'}>
        {isDisabledInPms && (
          <Tooltip label="Archived in PMS" withinPortal withArrow>
            <Center>
              <Icon
                icon="AlertTriangleIcon"
                size={12}
                color={colors.orange[7]}
              />
            </Center>
          </Tooltip>
        )}

        <Box>
          <Badge
            color="yellow"
            leftIcon={
              <Icon
                icon="CrossCircleIcon"
                size={12}
                color={(theme) => theme.colors.yellow[8]}
              />
            }
          >
            Archived
          </Badge>
        </Box>
      </Group>
    );
  }

  return (
    <Group wrap="nowrap" gap={'xs'}>
      {isDisabledInPms && (
        <Tooltip label="Archived in PMS" withinPortal withArrow>
          <Center>
            <Icon icon="AlertTriangleIcon" size={12} color={colors.orange[7]} />
          </Center>
        </Tooltip>
      )}

      <Box>
        <Badge color="green">Active on {appName}</Badge>
      </Box>
    </Group>
  );
};
